import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { pagedItems } from "../utils/adminPaging";
import { withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";
import {
  formatMoneyByCurrency,
  moneyByCurrencyFrom,
  formatBatchAvgValue,
  CurrencyTotals,
} from "../utils/currency";
import { TIME_RANGE_OPTIONS, TimeRangeKey, rangeStart } from "../utils/dateRange";
import TimeRangeBar from "./TimeRangeBar";
import UndatedNote from "./UndatedNote";
import StatsPending from "./admin-stats/StatsPending";
import { overviewFrom, statsUrl } from "./admin-stats/api";
import { useAdminRead } from "../hooks/useAdminRead";
import { absMediaUrl } from "../utils/mediaUrl";

interface Props {
  // Every talent account (the paged /users total), not the loaded page.
  // null = the dashboard couldn't load it (same for adminsCount).
  talentTotal: number | null;
  adminsCount: number | null;
  // Shared, parent-owned time window so the selection persists across the
  // Dashboard / Revenue / All Payouts tabs.
  range: TimeRangeKey;
  onRangeChange: (key: TimeRangeKey) => void;
  setTab: (tab: "orders" | "users" | "overview" | "admins" | "revenue" | "allPayouts") => void;
}

interface FanPayStats {
  range: string;
  total: number;
  methods: Record<string, number>;
  withEmail: number;
  returning: number;
  fingerprinted: number;
}

// Fixed display order; `auto` marks methods that hand us the fan's email
// automatically (green bars) vs typed cards / unstamped legacy rows (gray).
const FAN_PAY_METHODS: Array<{ key: string; label: string; auto: boolean }> = [
  { key: "apple_pay", label: " Apple Pay", auto: true },
  { key: "google_pay", label: "Google Pay", auto: true },
  { key: "link", label: "Link", auto: true },
  { key: "card", label: "Typed card", auto: false },
  { key: "unknown", label: "Unstamped (older)", auto: false },
];

interface OverviewPayout {
  _id: string;
  batchId: string;
  createdAt: string;
  dj: { _id: string; name: string; avatarUrl?: string };
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
  // Per-currency breakdown sent by the server (gross/fee/net per currency).
  // Authoritative source for rendering each payout row in its real currency.
  totalsByCurrency?: Record<string, CurrencyTotals>;
}

export default function AdminOverviewTab({ talentTotal, adminsCount, range, onRangeChange, setTab }: Props) {
  const navigate = useNavigate();
  const [recentPayouts, setRecentPayouts] = useState<OverviewPayout[]>([]);
  const [recentPayoutsFailed, setRecentPayoutsFailed] = useState(false);

  // The newest 10 only: the server pages /payouts and sends them newest first.
  const loadRecentPayouts = useCallback(() => {
    setRecentPayoutsFailed(false);
    withQuietRetry(() => adminApi.get("/payouts?limit=10"))
      .then((r) => setRecentPayouts(pagedItems<OverviewPayout>(r.data).items.slice(0, 10)))
      .catch(() => setRecentPayoutsFailed(true));
  }, []);
  useEffect(loadRecentPayouts, [loadRecentPayouts]);

  // 💳 How fans pay (fan profiles Phase 3) — server-computed, with its own
  // 30d/All-time toggle (independent of TimeRangeBar because "returning
  // fans" needs an all-time card-fingerprint lookback the browser doesn't
  // have). Each range is fetched once and cached for the session.
  const [fanPayRange, setFanPayRange] = useState<"30d" | "all">("30d");
  const [fanPay, setFanPay] = useState<Record<string, FanPayStats | null>>({});
  const [fanPayFailed, setFanPayFailed] = useState<Record<string, boolean>>({});
  const [fanPayAttempt, setFanPayAttempt] = useState(0);
  useEffect(() => {
    if (fanPay[fanPayRange]) return;
    const r0 = fanPayRange;
    setFanPayFailed((prev) => ({ ...prev, [r0]: false }));
    withQuietRetry(() => adminApi.get(`/fan-pay-stats?range=${r0}`))
      .then((r) => setFanPay((prev) => ({ ...prev, [r0]: r.data })))
      .catch(() => setFanPayFailed((prev) => ({ ...prev, [r0]: true })));
  }, [fanPayRange, fanPay, fanPayAttempt]);
  const fanPayStats = fanPay[fanPayRange] || null;

  // The KPI cards, the Order Statuses card and Top Talent for the selected
  // window, added up on the server over the whole history (lib/admin/stats.js):
  // paid & not refunded orders only for the money, each currency on its own,
  // every order for the counts. The window starts where it always did
  // (rangeStart, the admin's own time zone); All Time also counts orders with
  // no date and the note below says how many.
  const since = useMemo(() => rangeStart(range), [range]);
  const { data: stats, failed, retry } = useAdminRead(statsUrl("overview", since), overviewFrom);

  // Short label for the active window, reused in card sublabels + section titles.
  const activeRangeLabel =
    TIME_RANGE_OPTIONS.find((o) => o.key === range)?.label ?? "All Time";

  return (
    <div className="adm-tab-container fade-in">
      {/* Time-range filter — governs every summary number on this tab. */}
      <TimeRangeBar value={range} onChange={onRangeChange} />

      {stats && range === "all" && (
        <UndatedNote count={stats.kpis.undatedPaidCount} noun="paid order" />
      )}

      {/* KPI row */}
      {!stats ? (
        <StatsPending failed={failed} onRetry={retry} />
      ) : (
      <section className="adm-grid-5">
        <div className="kpi-card kpi-card-highlight" onClick={() => setTab("revenue")}>
          <div className="kpi-icon green-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Platform Earnings</div>
            <div className="kpi-value adm-green">
              {formatMoneyByCurrency(stats.kpis.earningsByCurrency)}
            </div>
            <div className="kpi-sublabel">{stats.kpis.paidCount} paid orders</div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("revenue")}>
          <div className="kpi-icon blue-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <path d="M18 20V10M12 20V4M6 20v-6" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Total Volume</div>
            <div className="kpi-value">{formatMoneyByCurrency(stats.kpis.volumeByCurrency)}</div>
            <div className="kpi-sublabel">{activeRangeLabel}</div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("revenue")}>
          <div className="kpi-icon purple-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Talent Payouts</div>
            <div className="kpi-value">{formatMoneyByCurrency(stats.kpis.talentPayoutsByCurrency)}</div>
            {/* The share varies by tier (90% at Bronze), so no number here. */}
            <div className="kpi-sublabel">Talent's share</div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("orders")}>
          <div className="kpi-icon orange-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Orders</div>
            <div className="kpi-value">{stats.kpis.orderCount}</div>
            <div className="kpi-sublabel">
              {stats.statusMix.selected > 0 ? `${stats.statusMix.selected} selected (unpaid)` : activeRangeLabel}
            </div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("users")}>
          <div className="kpi-icon blue-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Total Talent</div>
            {/* null = the dashboard's read failed: "—", never a made-up 0. */}
            <div className="kpi-value">{talentTotal === null ? "—" : talentTotal.toLocaleString()}</div>
            <div className="kpi-sublabel">{adminsCount === null ? "Couldn't load admins" : `${adminsCount} admins`}</div>
          </div>
        </div>
      </section>
      )}

      {/* 💳 How fans pay — payment mix + auto-email coverage + returning
          fans (fan profiles Phase 3). Founder-placed: right under the KPI
          row, with its own 30d/All-time toggle. */}
      <section className="adm-card" style={{ marginTop: 16 }}>
        <div className="card-header-flex adm-mb-12">
          <h2 className="adm-section-title">💳 How Fans Pay</h2>
          <div style={{ display: "flex", gap: 6 }}>
            {(["30d", "all"] as const).map((r) => (
              <button
                key={r}
                onClick={() => setFanPayRange(r)}
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  padding: "4px 12px",
                  borderRadius: 999,
                  cursor: "pointer",
                  border: fanPayRange === r ? "1px solid #10b981" : "1px solid #d6dce4",
                  background: fanPayRange === r ? "#10b981" : "transparent",
                  color: fanPayRange === r ? "#fff" : "#6b7280",
                }}
              >
                {r === "30d" ? "Last 30 days" : "All time"}
              </button>
            ))}
          </div>
        </div>
        {!fanPayStats && fanPayFailed[fanPayRange] ? (
          <AdminLoadFailed inline what="how fans pay" onRetry={() => setFanPayAttempt((n) => n + 1)} />
        ) : !fanPayStats ? (
          <div className="adm-grey" style={{ fontSize: 13 }}>Loading…</div>
        ) : fanPayStats.total === 0 ? (
          <div className="adm-grey" style={{ fontSize: 13 }}>No paid requests in this window.</div>
        ) : (
          <>
            {FAN_PAY_METHODS.filter((m) => (fanPayStats.methods[m.key] || 0) > 0).map((m) => {
              const n = fanPayStats.methods[m.key] || 0;
              const pct = Math.round((n / fanPayStats.total) * 100);
              return (
                <div
                  key={m.key}
                  className="adm-mt-8" style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}
                >
                  <span className="adm-bold" style={{ width: 118, color: "#374151", flexShrink: 0 }}>
                    {m.label}
                  </span>
                  <div className="adm-flex-1" style={{ height: 12, borderRadius: 6, background: "#eef1f5", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        borderRadius: 6,
                        background: m.auto ? "#10b981" : "#9ca3af",
                      }}
                    />
                  </div>
                  <span style={{ width: 38, textAlign: "right", fontWeight: 800, color: "#111827" }}>
                    {pct}%
                  </span>
                  <span className="adm-grey" style={{ width: 44, textAlign: "right", fontSize: 11 }}>{n}</span>
                </div>
              );
            })}
            <div style={{ borderTop: "1px solid #eef1f5", marginTop: 12, paddingTop: 10, fontSize: 12.5, color: "#6b7280", lineHeight: 1.55 }}>
              📧 <b style={{ color: "#10b981", fontSize: 14 }}>{Math.round((fanPayStats.withEmail / fanPayStats.total) * 100)}%</b> of
              paid requests came with a fan email — those fans get receipts &amp; status updates automatically.
            </div>
            <div className="adm-mt-6" style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.55 }}>
              ↩️ <b style={{ color: "#111827", fontSize: 14 }}>{Math.round((fanPayStats.returning / fanPayStats.total) * 100)}%</b> of
              requests are from returning fans (a card we've seen before).
            </div>
          </>
        )}
      </section>

      {/* Insights + Statuses */}
      {stats && (
      <section className="adm-overview-grid">
        <div className="adm-card overview-insights">
          <div className="card-header-flex adm-mb-12">
            <h2 className="adm-section-title">Top Talent — Revenue ({activeRangeLabel})</h2>
          </div>
          <div className="top-djs-list">
            {stats.topTalent.map((dj, idx) => {
              const src = absMediaUrl(dj.avatarUrl);
              return (
                <div
                  key={dj.id ?? "unknown"}
                  className="top-dj-row"
                  // Deleted talent share one "Unknown" line: no profile to open.
                  onClick={dj.id ? () => navigate(`/admin/dj/${dj.id}`) : undefined}
                  title={dj.id ? `Open ${dj.name}'s profile` : undefined}
                  style={dj.id ? undefined : { cursor: "default" }}
                >
                  <div className="dj-info-side">
                    <span className="rank-num">#{idx + 1}</span>
                    {src ? (
                      <img src={src} className="tiny-avatar" alt="" />
                    ) : (
                      <div className="tiny-placeholder">{(dj.name[0] || "?").toUpperCase()}</div>
                    )}
                    <span className="dj-name-sm dj-link-text">{dj.name}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar-fill" style={{ width: `${dj.barPct}%` }} />
                  </div>
                  <div className="dj-stat-side">
                    <span className="money-val">{formatMoneyByCurrency(dj.amountByCurrency, { decimals: 0 })}</span>
                    <span className="count-val">{dj.count} orders</span>
                  </div>
                </div>
              );
            })}
            {stats.topTalent.length === 0 && (
              <p className="muted-text adm-center adm-p-20">
                No paid orders in this range
              </p>
            )}
          </div>
        </div>

        <div className="adm-card overview-quick">
          <h2 className="adm-section-title adm-mb-14">
            Order Statuses
          </h2>
          <div className="status-grid">
            {Object.entries(stats.statusMix).map(([status, count]) => (
              <div key={status} className="status-item">
                <span className={`dot dot-${status}`} />
                <span className="status-name">{status}</span>
                <span className="status-count">{count}</span>
              </div>
            ))}
          </div>
          <div className="quick-actions-footer">
            <button className="adm-btn-text" onClick={() => setTab("orders")}>
              View All Orders →
            </button>
          </div>
        </div>
      </section>
      )}

      {/* Recent Payouts */}
      <section className="adm-card">
        <div className="card-header-flex adm-mb-12">
          <h2 className="adm-section-title">Recent Payouts</h2>
          <button className="adm-btn-text" onClick={() => setTab("allPayouts")}>
            View All →
          </button>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table-light adm-table-compact adm-w-full">
            <thead>
              <tr>
                <th>Date</th>
                <th style={{ width: "30%" }}>Talent</th>
                <th>Gross</th>
                <th>Net (Talent)</th>
                <th># Orders</th>
                <th>Avg. Value</th>
              </tr>
            </thead>
            <tbody>
              {recentPayouts.map((p) => {
                const dj = p.dj;
                const src = absMediaUrl(dj?.avatarUrl);
                // Render each payout in its real settlement currency from the
                // server's per-currency breakdown; fall back to the legacy
                // single number (with $) only if the breakdown is absent.
                const hasBreakdown = Object.keys(p.totalsByCurrency || {}).length > 0;
                const grossStr = hasBreakdown
                  ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "gross"))
                  : `$${p.totalGross.toFixed(2)}`;
                const netStr = hasBreakdown
                  ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "net"))
                  : `$${p.totalNet.toFixed(2)}`;
                const avgStr = formatBatchAvgValue(p.totalsByCurrency, p.avgRequestValue);
                return (
                  <tr key={p._id} className="hover-row">
                    <td className="muted-text adm-fs-78 adm-nowrap">
                      {new Date(p.createdAt).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" })}
                    </td>
                    <td>
                      {dj ? (
                        <div
                          className="dj-cell dj-cell-compact clickable"
                          onClick={() => navigate(`/admin/dj/${dj._id}`)}
                        >
                          {src ? (
                            <img src={src} className="dj-cell-avatar-sm" alt="" />
                          ) : (
                            <div className="dj-cell-placeholder-sm">{dj.name?.[0]?.toUpperCase() || "?"}</div>
                          )}
                          <span className="dj-link-text adm-fs-80 adm-semibold">
                            {dj.name}
                          </span>
                        </div>
                      ) : (
                        <span className="muted-text">Unknown</span>
                      )}
                    </td>
                    <td className="adm-semibold adm-fs-80">{grossStr}</td>
                    <td className="adm-bold adm-green adm-fs-80">
                      {netStr}
                    </td>
                    <td className="adm-fs-80">
                      <span className="adm-bold">{p.ordersCount}</span>{" "}
                      <span className="adm-fs-78">({p.ordersAccepted}/{p.ordersDeclined})</span>
                    </td>
                    <td className="adm-fs-80">{avgStr}</td>
                  </tr>
                );
              })}
              {recentPayouts.length === 0 && recentPayoutsFailed && (
                <tr>
                  <td colSpan={6}>
                    <AdminLoadFailed what="recent payouts" onRetry={loadRecentPayouts} />
                  </td>
                </tr>
              )}
              {recentPayouts.length === 0 && !recentPayoutsFailed && (
                <tr>
                  <td colSpan={6} className="adm-center adm-p-24 adm-grey">
                    No payouts yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
